import { useState, useRef, useCallback, useEffect } from "react";

// Type definitions for Web Speech API
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  serviceURI?: string;
  shouldAutoRestart?: boolean;
  start: () => void;
  stop: () => void;
  abort?: () => void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any)
    | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any)
    | null;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

// Legacy ParsedCommand type for backward compatibility
export type ParsedCommand =
  | { command: "NEXT_QUESTION" }
  | { command: "PREVIOUS_QUESTION" }
  | { command: "SELECT_RATING"; value: string }
  | { command: "SELECT_LEVEL"; value: string }
  | { command: "CLEAR_RESPONSE" }
  | { command: "SAVE_DRAFT" }
  | { command: "SUBMIT_ASSESSMENT" }
  | { command: "TEXT_INPUT"; text: string }
  | { command: "UNKNOWN" };

// Legacy parseCommand function for backward compatibility
export const parseCommand = (rawTranscript: string): ParsedCommand => {
  const lowerText = rawTranscript.toLowerCase().trim();

  if (!lowerText) {
    return { command: "UNKNOWN" };
  }

  // Navigation commands
  if (
    lowerText.includes("next question") ||
    lowerText.includes("go next") ||
    lowerText === "next"
  ) {
    return { command: "NEXT_QUESTION" };
  }
  if (
    lowerText.includes("previous question") ||
    lowerText.includes("previous") ||
    lowerText.includes("go back") ||
    lowerText === "back"
  ) {
    return { command: "PREVIOUS_QUESTION" };
  }
  if (
    lowerText.includes("submit") ||
    (lowerText.includes("finish") && lowerText.includes("submit")) ||
    lowerText.includes("submit questionnaire")
  ) {
    return { command: "SUBMIT_ASSESSMENT" };
  }
  if (lowerText.includes("save draft") || lowerText.includes("save")) {
    return { command: "SAVE_DRAFT" };
  }
  if (
    lowerText.includes("clear") ||
    lowerText.includes("delete") ||
    lowerText.includes("reset") ||
    lowerText === "erase"
  ) {
    return { command: "CLEAR_RESPONSE" };
  }

  // Level detection
  const levelMatch = lowerText.match(
    /level\s*(zero|one|two|three|four|five|six|seven|0|1|2|3|4|5|6|7)/i
  );
  if (levelMatch) {
    const levelText = levelMatch[1].toLowerCase();
    const levelMap: Record<string, string> = {
      zero: "L0",
      "0": "L0",
      one: "L1",
      "1": "L1",
      two: "L2",
      "2": "L2",
      three: "L3",
      "3": "L3",
      four: "L4",
      "4": "L4",
      five: "L5",
      "5": "L5",
      six: "L6",
      "6": "L6",
      seven: "L7",
      "7": "L7",
    };
    const levelValue = levelMap[levelText];
    if (levelValue) {
      return { command: "SELECT_LEVEL", value: levelValue };
    }
  }

  // Rating detection
  const numberMatch = rawTranscript.match(/\b(\d+)\b/);
  if (numberMatch) {
    const value = parseInt(numberMatch[0], 10);
    if (value >= 0 && value <= 10) {
      return { command: "SELECT_RATING", value: String(value) };
    }
  }

  // Text input
  if (rawTranscript.trim().length > 3) {
    return { command: "TEXT_INPUT", text: rawTranscript.trim() };
  }

  return { command: "UNKNOWN" };
};

const useSpeechToText = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const isLoading = false;
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);

  // Alternative: Use Web Speech API as fallback
  const initializeWebSpeechAPI = useCallback(() => {
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      setError("Speech recognition is not supported in this browser");
      setIsSupported(false);
      return null;
    }
    const SpeechRecognitionCtor = (window.SpeechRecognition ||
      window.webkitSpeechRecognition) as
      | (new () => SpeechRecognition)
      | undefined;
    if (!SpeechRecognitionCtor) {
      setError("Speech recognition is not supported in this browser");
      setIsSupported(false);
      return null;
    }
    const recognition = new SpeechRecognitionCtor();

    // Enable continuous mode for better stability
    recognition.continuous = true; // Keep listening continuously
    recognition.interimResults = true; // Enable interim results for better UX
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    // Try to enable offline/local recognition if available
    if ("webkitSpeechRecognition" in window) {
      // Chrome-specific settings for better reliability
      (recognition as any).serviceURI = "built-in"; // Try to use built-in engine
    }
    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      lastError.current = null; // Reset error on successful start
    };
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " "; // Add space between final results
        }
      }

      // Only update with final results to avoid too frequent updates
      if (finalTranscript.trim()) {
        setTranscript(prev => {
          const newTranscript = prev
            ? prev + " " + finalTranscript.trim()
            : finalTranscript.trim();
          return newTranscript;
        });
      }
    };
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // Handle different error types more gracefully
      lastError.current = event.error;
      switch (event.error) {
        case "network":
          // Don't disable listening for network errors - just show message
          setError(
            "🌐 Network issue - will retry automatically. You can also type your response."
          );
          break;
        case "not-allowed":
          setError(
            "🎤 Microphone access denied. Please allow microphone access and try again."
          );
          setIsListening(false);
          break;
        case "no-speech":
        case "aborted":
          setError(null);
          break;
        case "audio-capture":
          setError(
            "🎤 Microphone not available. Please check your microphone connection."
          );
          setIsListening(false);
          break;
        case "service-not-allowed":
          setError(
            "🚫 Speech recognition service not available. Please try again later."
          );
          setIsListening(false);
          break;
        default:
          setError(null); // Don't show error, just continue
          break;
      }
    };
    recognition.onend = () => {
      setIsListening(false);

      // Only stop auto-restart for critical errors (not network or no-speech)
      const criticalErrors = [
        "not-allowed",
        "audio-capture",
        "service-not-allowed",
      ];
      if (lastError.current && criticalErrors.includes(lastError.current)) {
        recognition.shouldAutoRestart = false;
        return;
      }

      // Don't auto-restart if it was aborted (likely due to manual stop or restart)
      if (lastError.current === "aborted") {
        // Reset error after a moment
        setTimeout(() => {
          lastError.current = null;
        }, 100);
        return;
      }

      // Auto-restart for network errors and normal endings to maintain continuous mode
      // Only if shouldAutoRestart is true and we didn't just abort
      if (recognition.shouldAutoRestart && lastError.current !== "aborted") {
        // Longer delay to avoid race conditions
        setTimeout(() => {
          // Double-check that we should still restart
          if (
            recognition.shouldAutoRestart &&
            lastError.current !== "aborted"
          ) {
            try {
              recognition.start();
            } catch (err: any) {
              // Only disable on repeated failures
              if (err.message.includes("already started")) {
                // Recognition already active, continue
              } else if (err.message.includes("abort")) {
                recognition.shouldAutoRestart = false;
              } else {
                // For other errors, try once more after a longer delay
                setTimeout(() => {
                  if (recognition.shouldAutoRestart) {
                    try {
                      recognition.start();
                    } catch (retryErr: any) {
                      recognition.shouldAutoRestart = false;
                    }
                  }
                }, 2000);
              }
            }
          }
        }, 1000); // Longer delay before restarting to avoid conflicts
      }
    };

    // Store auto-restart flag
    recognition.shouldAutoRestart = false;
    return recognition;
  }, []);

  const recognition = useRef<SpeechRecognition | null>(null);
  const lastError = useRef<string | null>(null);

  // Start listening - ENHANCED ERROR HANDLING
  const startListening = useCallback(async () => {
    try {
      setError(null);

      // Check if HTTPS (required for speech recognition in production)
      if (
        window.location.protocol !== "https:" &&
        window.location.hostname !== "localhost"
      ) {
        throw new Error(
          "🔒 Voice mode requires HTTPS connection or localhost. Current: " +
            window.location.protocol
        );
      }

      // Stop any existing recognition first to prevent conflicts
      if (recognition.current) {
        try {
          // Check if it's already running
          if (isListening) {
            recognition.current.shouldAutoRestart = false; // Disable auto-restart before stopping
            recognition.current.stop();
            await new Promise(resolve => setTimeout(resolve, 500)); // Longer delay to ensure clean stop
          }
        } catch (stopErr) {
          // Error stopping recognition (may not be running)
        }
      }

      // Use Web Speech API as it's more reliable and easier to implement
      if (!recognition.current) {
        recognition.current = initializeWebSpeechAPI();
      }

      if (recognition.current) {
        // Clear any previous error state
        lastError.current = null;
        // Enable auto-restart by default when starting
        recognition.current.shouldAutoRestart = true;

        try {
          recognition.current.start();
        } catch (startErr: any) {
          // If already started, that's okay - continue
          if (
            !startErr.message ||
            !startErr.message.includes("already started")
          ) {
            throw startErr;
          }
        }
      } else {
        throw new Error("Speech recognition is not available");
      }
    } catch (err: any) {
      console.error("🚨 Failed to start speech recognition:", err);
      setError(err.message);
      setIsListening(false);
    }
  }, [initializeWebSpeechAPI, isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognition.current) {
      recognition.current.shouldAutoRestart = false; // Prevent auto-restart
      try {
        recognition.current.stop();
        // Clear error state when manually stopping
        lastError.current = null;
      } catch (err) {
        // Error stopping recognition
      }
    }
    setIsListening(false);
  }, []);

  // Enable continuous listening mode
  const enableContinuousMode = useCallback(() => {
    if (recognition.current) {
      recognition.current.shouldAutoRestart = true;
    }
  }, []);

  // Disable continuous listening mode
  const disableContinuousMode = useCallback(() => {
    if (recognition.current) {
      recognition.current.shouldAutoRestart = false;
    }
  }, []);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript("");
  }, []);

  // Append to transcript (for manual additions)
  const appendToTranscript = useCallback((text: string) => {
    setTranscript(prev => prev + (prev ? " " : "") + text);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Voice commands processing
  const processVoiceCommand = useCallback((text: string) => {
    const lowerText = text.toLowerCase().trim();

    // Check for exact matches first (most specific)
    if (lowerText === "next" || lowerText === "go next") {
      return { type: "NEXT_QUESTION" };
    }
    if (
      lowerText === "previous" ||
      lowerText === "back" ||
      lowerText === "go back"
    ) {
      return { type: "PREVIOUS_QUESTION" };
    }

    // Navigation commands (check for phrases)
    if (
      lowerText.includes("next question") ||
      lowerText.includes("go next") ||
      lowerText.startsWith("next ")
    ) {
      return { type: "NEXT_QUESTION" };
    }
    if (
      lowerText.includes("previous question") ||
      lowerText.includes("go back") ||
      lowerText.startsWith("previous ") ||
      lowerText.startsWith("back ")
    ) {
      return { type: "PREVIOUS_QUESTION" };
    }

    // Action commands
    if (
      lowerText.includes("submit questionnaire") ||
      (lowerText.includes("finish") && lowerText.includes("submit"))
    ) {
      return { type: "SUBMIT" };
    }
    if (lowerText === "submit" || lowerText.startsWith("submit ")) {
      return { type: "SUBMIT" };
    }
    if (
      lowerText.includes("save draft") ||
      (lowerText.includes("save") && !lowerText.includes("select"))
    ) {
      return { type: "SAVE_DRAFT" };
    }
    if (
      lowerText === "clear" ||
      lowerText === "delete" ||
      lowerText === "reset" ||
      lowerText === "erase" ||
      lowerText.startsWith("clear ") ||
      lowerText.startsWith("delete ") ||
      lowerText.startsWith("reset ")
    ) {
      return { type: "CLEAR_RESPONSE" };
    }

    // Confirmation page commands
    if (
      lowerText.includes("review again") ||
      lowerText.includes("review") ||
      lowerText.includes("go back")
    ) {
      return { type: "REVIEW_AGAIN" };
    }
    if (lowerText.includes("cancel") || lowerText.includes("abort")) {
      return { type: "CANCEL_SUBMIT" };
    }

    // Enhanced Level commands - look for explicit level commands first (for XFactor questions)
    if (lowerText.includes("level")) {
      const levelMatch = lowerText.match(
        /level\s*(zero|one|two|three|four|five|six|seven|0|1|2|3|4|5|6|7)/i
      );
      if (levelMatch) {
        const levelText = levelMatch[1].toLowerCase();
        const levelMap: Record<string, string> = {
          zero: "L0",
          "0": "L0",
          one: "L1",
          "1": "L1",
          two: "L2",
          "2": "L2",
          three: "L3",
          "3": "L3",
          four: "L4",
          "4": "L4",
          five: "L5",
          "5": "L5",
          six: "L6",
          "6": "L6",
          seven: "L7",
          "7": "L7",
        };
        const levelCode = levelMap[levelText];
        if (levelCode) {
          return { type: "SELECT_LEVEL", value: levelCode };
        }
      }
    }
    // Direct number detection for ratings (only when it's a standalone number or clear rating context)
    const numberMatch = text.match(/\b(\d+)\b/);
    if (numberMatch) {
      const value = parseInt(numberMatch[0], 10);
      if (value >= 1 && value <= 5) {
        return { type: "SELECT_RATING", value };
      }
    }
    // Enhanced number recognition for ratings - only when used in rating context
    const ratingWords: Record<string, number> = {
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
    };
    // Only detect number words if they appear to be standalone or in rating context
    for (const [word, number] of Object.entries(ratingWords)) {
      // Check if the word appears as a standalone word (not part of another word)
      const wordRegex = new RegExp(`\\b${word}\\b`, "i");
      if (
        wordRegex.test(lowerText) &&
        !lowerText.includes("to ") &&
        !lowerText.includes("for ")
      ) {
        return { type: "SELECT_RATING", value: number };
      }
    }

    // Rating by word patterns (very satisfied = 5, satisfied = 4, etc.)
    if (
      lowerText.includes("very satisfied") ||
      lowerText.includes("definitely yes") ||
      lowerText.includes("very good")
    ) {
      return { type: "SELECT_RATING", value: 5 };
    }
    if (
      lowerText.includes("satisfied") ||
      lowerText.includes("probably yes") ||
      lowerText.includes("good")
    ) {
      return { type: "SELECT_RATING", value: 4 };
    }
    if (
      lowerText.includes("neutral") ||
      lowerText.includes("maybe") ||
      lowerText.includes("okay")
    ) {
      return { type: "SELECT_RATING", value: 3 };
    }
    if (
      lowerText.includes("dissatisfied") ||
      lowerText.includes("probably not") ||
      lowerText.includes("poor")
    ) {
      return { type: "SELECT_RATING", value: 2 };
    }
    if (
      lowerText.includes("very dissatisfied") ||
      lowerText.includes("definitely not") ||
      lowerText.includes("very poor")
    ) {
      return { type: "SELECT_RATING", value: 1 };
    }

    // Option selection commands - improved patterns (check BEFORE text input)
    // Check for explicit selection commands first
    if (
      lowerText.includes("select") ||
      lowerText.includes("choose") ||
      lowerText.includes("pick")
    ) {
      const optionPatterns = [
        // "select option one", "choose first option", "pick option 2"
        /(?:select|choose|pick)\s+(?:option\s+)?(?:number\s+)?([a-z]+|\d+|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last)/i,
        // "select Marketing", "choose Sales"
        /(?:select|choose|pick)\s+(.+)/i,
      ];

      for (let i = 0; i < optionPatterns.length; i++) {
        const pattern = optionPatterns[i];
        const match = lowerText.match(pattern);
        if (match && match[1]) {
          const option = match[1].toLowerCase().trim();

          // Check if it's a number/ordinal
          let optionIndex = null;
          if (/^\d+$/.test(option)) {
            optionIndex = parseInt(option) - 1; // Convert to 0-based index
          } else {
            const ordinals: Record<string, number> = {
              first: 0,
              second: 1,
              third: 2,
              fourth: 3,
              fifth: 4,
              sixth: 5,
              seventh: 6,
              eighth: 7,
              ninth: 8,
              tenth: 9,
              one: 0,
              two: 1,
              three: 2,
              four: 3,
              five: 4,
              six: 5,
              seven: 6,
              eight: 7,
              nine: 8,
              ten: 9,
              last: -1,
            };
            optionIndex =
              ordinals[option] !== undefined ? ordinals[option] : null;
          }

          if (optionIndex !== null) {
            return { type: "SELECT_OPTION", index: optionIndex };
          } else {
            // It's an option name
            return { type: "SELECT_OPTION", value: option, index: undefined };
          }
        }
      }
    }

    // Check for "option X" or "first option" patterns
    const optionPatterns = [
      /(?:option|choice)\s+(?:number\s+)?([a-z]+|\d+|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last)/i,
      /^(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last)\s+(?:option|choice)/i,
      /^number\s+([a-z]+|\d+)/i,
    ];

    for (let i = 0; i < optionPatterns.length; i++) {
      const pattern = optionPatterns[i];
      const match = lowerText.match(pattern);
      if (match && match[1]) {
        const option = match[1].toLowerCase();
        let optionIndex = null;

        if (/^\d+$/.test(option)) {
          optionIndex = parseInt(option) - 1;
        } else {
          const ordinals: Record<string, number> = {
            first: 0,
            second: 1,
            third: 2,
            fourth: 3,
            fifth: 4,
            sixth: 5,
            seventh: 6,
            eighth: 7,
            ninth: 8,
            tenth: 9,
            one: 0,
            two: 1,
            three: 2,
            four: 3,
            five: 4,
            six: 5,
            seven: 6,
            eight: 7,
            nine: 8,
            ten: 9,
            last: -1,
          };
          optionIndex =
            ordinals[option] !== undefined ? ordinals[option] : null;
        }

        if (optionIndex !== null) {
          return { type: "SELECT_OPTION", index: optionIndex };
        }
      }
    }

    // If it's a single word and not a command, might be an option name
    const singleWord = lowerText.split(/\s+/).length === 1;
    if (
      singleWord &&
      lowerText.length > 2 &&
      ![
        "next",
        "previous",
        "back",
        "submit",
        "save",
        "clear",
        "delete",
        "reset",
        "level",
      ].includes(lowerText)
    ) {
      return { type: "SELECT_OPTION", value: lowerText, index: undefined };
    }

    // If no specific command was detected, treat as text input for text areas
    // Only return text if it's substantial (more than just noise or single words)
    if (
      text.trim().length > 3 &&
      !lowerText.includes("level") &&
      !lowerText.includes("select")
    ) {
      return { type: "TEXT_INPUT", value: text.trim() };
    }

    return null;
  }, []);

  // Retry connection for network issues
  const retryConnection = useCallback(async () => {
    await stopListening();
    setTimeout(() => {
      startListening();
    }, 1000);
  }, [startListening, stopListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognition.current) {
        recognition.current.shouldAutoRestart = false;
        try {
          recognition.current.stop();
        } catch (err) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  return {
    isListening,
    transcript,
    isLoading,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
    appendToTranscript,
    clearError,
    processVoiceCommand,
    retryConnection,
    enableContinuousMode,
    disableContinuousMode,
    retryCount: 0,
    maxRetries: 3,
    // Backward compatibility aliases
    speechSupported: isSupported,
    parseCommand,
  };
};

export default useSpeechToText;
